import { Switch } from '@thanx/uikit/switch'
import { Text } from '@thanx/uikit/text'
import { useStyletron } from '@thanx/uikit/theme'
import FeedbackRatingIcon, {
  FeedbackRating,
} from 'components/FeedbackRatingIcon'
import { buildTranslate } from 'locales'
import { Fields as UserEvent } from 'models/UserEvent'
import moment from 'moment'
import React from 'react'
import { Button } from 'react-bootstrap'
import { Link } from 'react-router-dom'
import BodyCell from 'scenes/Reports/components/Table/BodyCell'
import BodyCellContainer from 'scenes/Reports/components/Table/BodyCellContainer'
import { merchantTheme } from 'theme'
import { getOrderRatingViewPath } from 'utilities/orderRatingViewPath'
import { getEventDescription } from './helpers'

const t = buildTranslate('users.view.activity.overview.table')

// AI-1289 (FR-32): the event's related_item carries the actual rating
// polarity, not just the hashid (models/UserEvent.ts's
// OrderRatingSubmittedEvent.related_item.rating) -- so the icon below reads
// the real up/down value instead of a fixed glyph, matching R4's S5 mock (the
// thumb flips with the rating). See ratingPolarity below for where it's read.

// The same rating has to open the same screen from every tab: the Orders tab
// and the Order feedback tab both feed getOrderRatingViewPath a real
// conversation_id, so this one does too. A conversation-backed rating opens
// the RecoveryAI case; one without opens the Reports rating detail.
function getFeedbackPath(event: UserEvent): string | null {
  if (event.type !== 'order_rating_submitted') return null

  return getOrderRatingViewPath({
    id: event.related_item.order_rating_id,
    conversationId: event.related_item.conversation_id,
  })
}

type Props = {
  event: UserEvent
  isLoading: boolean
  onOrderDetailsClick: (event: UserEvent) => void
}

function formatDate(date: string) {
  return {
    day: moment(date).format('MMM D, YYYY'),
    time: moment(date).format('h:mm a'),
  }
}

const Row: React.FC<Props> = ({ event, isLoading, onOrderDetailsClick }) => {
  const { occurred_at, title, type, location_address } = event
  const [css] = useStyletron()

  function handleOrderDetailsClick(e: React.MouseEvent<HTMLElement>) {
    e.preventDefault()
    onOrderDetailsClick(event)
  }

  const date = formatDate(occurred_at)
  const { colors } = merchantTheme

  const description = getEventDescription(event)
  const isOrderRating = type === 'order_rating_submitted'
  const feedbackPath = getFeedbackPath(event)
  // Which way the thumb points. Rendering a fixed glyph would show a
  // thumbs-down rating as a thumbs up, which is worse than showing none — the
  // server-built title reads the same for both directions, so the payload's
  // own polarity is the only thing that can distinguish them. Defaults to 'up'
  // only to keep the type total; the Switch above never renders for a
  // non-rating event.
  const ratingPolarity: FeedbackRating =
    event.type === 'order_rating_submitted' ? event.related_item.rating : 'up'

  // S5 (R4-prototype.md): `.event-desc` -- icon and title share one row,
  // gap 8px.
  const activityClass = css({
    alignItems: 'center',
    display: 'flex',
    gap: '8px',
  })

  return (
    <tr>
      <BodyCellContainer isErrored={false} isLoading={isLoading}>
        <Text.BodyText4 color={colors.grey70} tag="div">
          {date.day}
        </Text.BodyText4>
        <Text.BodyText5 color={colors.grey70} tag="div">
          {date.time}
        </Text.BodyText5>
      </BodyCellContainer>
      <BodyCellContainer isErrored={false} isLoading={isLoading}>
        <div className={activityClass}>
          <Switch condition={isOrderRating}>
            <FeedbackRatingIcon rating={ratingPolarity} />
          </Switch>
          <Text.BodyText4 color={colors.grey70} tag="div">
            {title}
          </Text.BodyText4>
        </div>
        <Switch condition={!!description}>
          <Text.BodyText5 color={colors.grey70} tag="div">
            {description}
          </Text.BodyText5>
        </Switch>
      </BodyCellContainer>
      <BodyCell
        isErrored={false}
        isLoading={isLoading}
        values={[{ value: location_address }]}
      />
      <BodyCellContainer
        className={'text-right'}
        isErrored={false}
        isLoading={isLoading}
      >
        {type === 'transacted' && (
          <Button
            onClick={handleOrderDetailsClick}
            className="d-flex align-items-center"
            bsStyle="link"
          >
            {t('order_details')}
          </Button>
        )}
        {feedbackPath && (
          <Link
            to={feedbackPath}
            className={css({
              color: merchantTheme.colors.belizeHole50,
              fontWeight: 400,
              textDecoration: 'none',
            })}
          >
            {t('view_feedback')}
          </Link>
        )}
      </BodyCellContainer>
    </tr>
  )
}

export default Row
